import React, { useContext, useMemo } from 'react'
import { Consumption } from 'contexts/consumption'
import { PercentageStrip } from 'components/percentageStrip'
import { Locale } from 'contexts/locale'
import {
  getMemoisedYearlyConsumption,
  getPureEthanolVolume
} from 'lib/calculations'
import { DrinkTypes } from 'types/DrinkTypes'
import alcohols from 'lib/alcohol.yml'
import consumptionByType, { CountryByType } from 'data/consumption-by-type.csv'
import { getClosestCountryConsumptionWise } from 'lib/getClosestCountry'
import totalConsumption from 'data/total-consumption.csv'
import styles from './styles.module.scss'
import IntlMessageFormat from 'intl-messageformat'

const dataKeys: (keyof Omit<CountryByType, 'Country' | 'Year'>)[] = [
  'Beer',
  'Wine',
  'Spirits',
  'Other'
]

export function Ratios() {
  const locale = useContext(Locale)
  const alcoholMessages = require('components/calculator/locales/' +
    locale +
    '.yml')
  const messages = require('./locales/' + locale + '.yml')
  const countryTranslations = require('locales/countries.' + locale + '.yml')
  const closestCountryTitleFormatter = new IntlMessageFormat(
    messages['titleClosestCountry'],
    locale + '-CH'
  )

  const consumption = useContext(Consumption)
  // Personal data
  const parts = dataKeys.map(key => {
    let amount = getPureEthanolVolume(
      consumption.drinks[key],
      key as DrinkTypes
    )

    return {
      amount,
      label: alcoholMessages.drinktype.default[key],
      color: alcohols[key as DrinkTypes].color
    }
  })

  // Switzerland data
  const switzerlandData = consumptionByType.find(
    value => value.Country === 'Switzerland'
  ) as CountryByType
  const switzerlandParts = dataKeys.map(key => ({
    amount: +switzerlandData[key] || 0,
    label: key,
    color: alcohols[key].color
  }))

  // Closest country data
  const yearlyConsumption = getMemoisedYearlyConsumption(consumption)
  const country = useMemo(
    () =>
      getClosestCountryConsumptionWise(
        yearlyConsumption,
        totalConsumption,
        consumption.isFemale
      ),
    [yearlyConsumption, consumption.isFemale]
  )
  const closestCountryData = consumptionByType.find(
    value => value.Country === country.Country
  ) as CountryByType
  const closestCountryParts = dataKeys.map(key => ({
    amount: +closestCountryData[key] || 0,
    label: key,
    color: alcohols[key].color
  }))

  return (
    <div className={styles.ratios}>
      <PercentageStrip
        parts={parts}
        locale={locale}
        title={messages['titleOwnConsumption']}
      />
      <PercentageStrip
        locale={locale}
        parts={switzerlandParts}
        title={messages['titleSwitzerland']}
        hideLegend
      />
      <PercentageStrip
        locale={locale}
        parts={closestCountryParts}
        title={closestCountryTitleFormatter.format({
          country: countryTranslations[closestCountryData.Country]
        })}
        hideLegend
      />
    </div>
  )
}
